import { format } from 'date-fns';
import { DATE_FORMAT } from 'src/components/DatePickerInput/DatePickerInput';
import {
  getBudgetResetPeriodForPayload,
  getDurationMinutes,
  getValidDaysOfWeekForPayload,
} from 'src/components/oneCorp/features/MealCredit/utils/mealCreditDurationsCalculations.utils';
import { ILocation } from 'src/types/Location';
import {
  AvailableOrderTypes,
  IMealCredit,
} from 'src/components/oneCorp/features/MealCredit/utils/EnterpriseMealCreditsDetailsPage.utils';
import { MealCreditInstanceType, LocTagInvite } from 'src/types/LineOfCredit';
import {
  ExpiresOn,
  MonthAvailability,
  TimeIntervalTypeValues,
} from 'src/components/oneCorp/features/MealCredit/common/formFields/mealCreditFormFields.utils';
import { MerchantRestrictionPayload } from 'src/types/OrderRestrictions';
import {
  transformFormToPayloadMerchantRestrictions,
  transformFormToPayloadItemRestrictions,
} from 'src/common/OrderRestrictions/orderRestrictions.utils';
import { BudgetResetTypeValues } from 'src/components/oneCorp/features/MealCredit/common/formFields/Select/BudgetResetTypeSelect/BudgetResetTypeSelect.utils';
import { getInstanceType } from 'src/components/oneCorp/features/MealCredit/utils/getInstanceType';
import { getFirstInstanceEndDateUtc } from 'src/components/oneCorp/features/MealCredit/utils/getFirstInstanceEndDateUtc';

export interface LocationMap {
  [locationId: string]: string;
}

export interface MealCreditPayload {
  owner_id: string;
  currency_code: string;
  billable_number_state: boolean;
  name: string;
  instance_type: MealCreditInstanceType;
  is_repeating: boolean;
  first_instance_utc: string;
  first_instance_end_date_utc?: string;
  expiration?: string;
  fulfillment_types: FulfillmentType[];
  budget_reset_period: number;
  duration_minutes: number;
  expense_codes_enabled: boolean;
  expense_codes_required: boolean;
  locations: string[];
  valid_days_of_week: number[];
  display_budgets: boolean;
  expense_comment_state: ExpenseCommentStateMap;
  expense_comments_required: boolean;
  tag_budgets: { [id: string]: string };
  valid_order_types: OrderTypesMap[];
  order_policy_acceptance_required?: boolean;
  prohibited_items: string[];
  predefined_reasons_enabled: boolean;
  send_loc_welcome_email: boolean;
  merchant_restrictions: MerchantRestrictionPayload[];
  item_restrictions?: string[];
}

export interface MealCreditUpdatePayload extends MealCreditPayload {
  id: string;
}

export type RestrictionsFormData = Record<string, string | boolean>;

export interface IMealCreditFormFields {
  name: string;
  budgetLength: MealCreditLength;
  startTime: string;
  startTimeMeridian: Meridian;
  endTime: string;
  endTimeMeridian: Meridian;
  timeIntervalType: TimeIntervalTypeValues;
  date: Date;
  frequency: string;
  days: boolean[];
  expiration?: Date;
  budgetResetPeriod: string;
  locations: LocationMap;
  locationsExistence: 'hasNoLocation' | 'hasLocation';
  fulfillmentType: FulfillmentType[];
  availableOrderTypes: AvailableOrderTypes[];
  orderPolicyAcceptanceRequired: RadioOption;
  displayBudgets: RadioOption;
  expenseCodes: RadioOptionExtended;
  expenseComments: RadioOptionExtended;
  expenseReason: RadioOption;
  sendLocWelcomeEmail: RadioOption;
  expiresOn: ExpiresOn;
  merchantRestrictions: RestrictionsFormData;
  itemRestrictions: RestrictionsFormData;
  budgetResetType: BudgetResetTypeValues;
  numberOfConsecutiveDays: number;
  numberOfConsecutiveMonths: number;
  monthAvailableOn: MonthAvailability;
  monthAvailableFrom: Date;
  monthAvailableTo: Date;
}

export enum Meridian {
  AM = 'AM',
  PM = 'PM',
}
export enum FulfillmentType {
  DELIVERY = 'Delivery',
  PICKUP = 'Pickup',
}

export enum ExpenseCommentStateMap {
  No = 'DISABLED',
  Optional = 'OPTIONAL',
  Yes = 'REQUIRED',
}
export enum ExpenseCommentState {
  DISABLED = 'No',
  OPTIONAL = 'Optional',
  REQUIRED = 'Yes',
}

export enum OrderTypesMap {
  'Regular' = 'STANDARD',
  'Catering' = 'CATERING',
  'Group' = 'GROUP',
  'Team' = 'GROUP',
}

export const DATE_PLACEHOLDER = DATE_FORMAT.toUpperCase();
export const TIME_FORMAT_SHORT = 'h:mm';
export const TIME_MERIDIAN_FORMAT = 'aa';
export const EVENT_MAX_WEEK_LENGTH = 52;
export const DAYS_SHORT = ['Mon', 'Tues', 'Wed', 'Thur', 'Fri', 'Sat', 'Sun'];
export const defaultDayTags: boolean[] = new Array(DAYS_SHORT.length).fill(
  true
);
export const MONTHLY_DEFAULT_BUDGET_RESET_PERIOD = 1;

export enum MealCreditLength {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
  CUSTOM = 'CUSTOM',
}

export enum RadioOption {
  YES = 'Yes',
  NO = 'No',
}

export enum RadioOptionExtended {
  YES = 'Yes',
  NO = 'No',
  OPTIONAL = 'Optional',
}

export const defaultFormValues = {
  startTime: '12:00',
  endTime: '11:59',
  startTimeMeridian: Meridian.AM,
  endTimeMeridian: Meridian.PM,
  timeIntervalType: TimeIntervalTypeValues.ALL_DAY,
  budgetResetPeriod: '',
  locations: null,
  locationsExistence: 'hasNoLocation',
  availableOrderTypes: [
    AvailableOrderTypes.STANDARD,
    AvailableOrderTypes.CATERING,
  ],
  orderPolicyAcceptanceRequired: RadioOption.NO,
  displayBudgets: RadioOption.YES,
  expenseCodes: RadioOptionExtended.OPTIONAL,
  expenseComments: RadioOptionExtended.NO,
  expenseReason: RadioOption.NO,
  expiresOn: ExpiresOn.NEVER,
  budgetResetType: BudgetResetTypeValues.DAYS,
  days: defaultDayTags,
  budgetLength: MealCreditLength.DAILY,
  numberOfConsecutiveDays: 1,
  numberOfConsecutiveMonths: MONTHLY_DEFAULT_BUDGET_RESET_PERIOD,
  monthAvailableOn: MonthAvailability.EVERY_DAY,
  monthAvailableFrom: null,
  monthAvailableTo: null,
} as IMealCreditFormFields;

export function transformToYesOrNoOrOptionalString(
  required: boolean,
  enabled: boolean
): RadioOptionExtended {
  if (!enabled) {
    return RadioOptionExtended.NO;
  }
  return required ? RadioOptionExtended.YES : RadioOptionExtended.OPTIONAL;
}

export function transformToYesOrNoString(required: boolean): RadioOption {
  return required ? RadioOption.YES : RadioOption.NO;
}

export function setTimeToDate(
  date: Date | undefined | null,
  time: string,
  meridian: Meridian
): Date {
  if (!date || !time || !meridian) {
    return null;
  }

  const newDate = new Date(date.getTime());
  const [hh, mm] = time.split(':');
  const hours = convertHoursTo24Hours(Number(hh), meridian);
  const minutes = Number(mm);
  newDate.setHours(hours, minutes);
  return newDate;
}

export function convertHoursTo24Hours(hh: number, meridian: Meridian): number {
  let hours = hh;
  if (meridian === Meridian.AM && hours === 12) {
    // catching edge-case of 12AM
    hours = 0;
  }

  if (meridian === 'PM') {
    // everything with PM can just be mod'd and added with 12 - the max will be 23
    hours = (hours % 12) + 12;
  }

  return hours;
}

export function getLocationsMap(locations: ILocation[] = []): LocationMap {
  return locations.reduce((map, { id, name }) => ({ ...map, [id]: name }), {});
}

type MapToPayloadProps = {
  baselinePayload?: Partial<MealCreditPayload>;
  values: IMealCreditFormFields;
  corpid: string;
  startDate: Date;
  isMultiDaysLengthBudget: boolean;
  hasLocation: boolean;
  isRepeating: boolean;
  orderPolicyId: string;
  budgets: LocTagInvite[];
  mealCreditProhibitedItems: string[];
  isMonthAvailable: boolean;
  monthAvailableTo: Date;
  expirationDate: Date;
};

export function mapToPayload({
  baselinePayload = {},
  values,
  corpid,
  startDate,
  isMultiDaysLengthBudget,
  hasLocation,
  isRepeating,
  orderPolicyId,
  budgets,
  mealCreditProhibitedItems,
  isMonthAvailable,
  monthAvailableTo,
  expirationDate,
}: MapToPayloadProps): MealCreditPayload {
  let tagBudgets = {};
  // if budgets are provided, transform them into what is expected by the back-end
  if (budgets) {
    tagBudgets = budgets.reduce(
      (budgetMap, budget) => ({
        ...budgetMap,
        [budget.id]: budget.budget,
      }),
      tagBudgets
    );
  }

  const payload: MealCreditPayload = {
    ...baselinePayload,
    owner_id: corpid,
    currency_code: 'USD',
    billable_number_state: null,
    name: values.name,
    is_repeating: isRepeating,
    first_instance_utc: format(startDate, "yyyy-MM-dd'T'HH:mm:ss'.000Z'"),
    expiration: expirationDate
      ? format(expirationDate, "yyyy-MM-dd'T'HH:mm:ss'.000Z'")
      : null,
    fulfillment_types: hasLocation
      ? values.fulfillmentType
      : [FulfillmentType.DELIVERY, FulfillmentType.PICKUP],
    expense_codes_enabled: values.expenseCodes !== RadioOptionExtended.NO,
    expense_codes_required: values.expenseCodes === RadioOptionExtended.YES,
    locations: values.locations ? Object.keys(values.locations) : null,
    display_budgets: values.displayBudgets === RadioOption.YES,
    expense_comment_state: ExpenseCommentStateMap[values.expenseComments],
    expense_comments_required:
      values.expenseComments === RadioOptionExtended.YES,
    tag_budgets: tagBudgets,
    valid_order_types: values.availableOrderTypes.map(
      (type) => OrderTypesMap[type]
    ),
    order_policy_acceptance_required:
      values.orderPolicyAcceptanceRequired === RadioOption.YES,
    prohibited_items: mealCreditProhibitedItems,
    predefined_reasons_enabled: values.expenseReason === RadioOption.YES,
    send_loc_welcome_email: values.sendLocWelcomeEmail === RadioOption.YES,
    merchant_restrictions: transformFormToPayloadMerchantRestrictions(
      values.merchantRestrictions
    ),
    item_restrictions: transformFormToPayloadItemRestrictions(
      values.itemRestrictions
    ),
    duration_minutes: getDurationMinutes(values),
    valid_days_of_week: getValidDaysOfWeekForPayload({
      days: values.days,
      isRepeating,
      isMultiDaysLengthBudget,
    }),
    budget_reset_period: getBudgetResetPeriodForPayload({
      budgetLength: values.budgetLength,
      numberOfConsecutiveDays: values.numberOfConsecutiveDays,
      budgetResetType: values.budgetResetType,
      budgetResetPeriod: values.budgetResetPeriod,
    }),
    instance_type: getInstanceType({
      budgetLength: values.budgetLength,
      budgetResetType: values.budgetResetType,
    }),
    ...(isMonthAvailable && {
      first_instance_end_date_utc: getFirstInstanceEndDateUtc({
        isMonthAvailable,
        monthAvailableOn: values.monthAvailableOn,
        monthAvailableTo,
      }),
    }),
  };
  if (!orderPolicyId) {
    delete payload.order_policy_acceptance_required;
  }
  return payload;
}

export function getBudgetLength(mealCredit: IMealCredit): MealCreditLength {
  if (mealCredit.instance_type === MealCreditInstanceType.DAILY) {
    return MealCreditLength.DAILY;
  }
  if (mealCredit.instance_type === MealCreditInstanceType.WEEKLY) {
    if (mealCredit.budget_reset_period === defaultDayTags.length) {
      return MealCreditLength.WEEKLY;
    }
    return MealCreditLength.CUSTOM;
  }
  if (mealCredit.instance_type === MealCreditInstanceType.MONTHLY) {
    return mealCredit.budget_reset_period > MONTHLY_DEFAULT_BUDGET_RESET_PERIOD
      ? MealCreditLength.CUSTOM
      : MealCreditLength.MONTHLY;
  }
  return null;
}
